import posthog from "posthog-js";
import { useEffect } from "react";

/**
 * Both variants sell the same thing — better matches — but from different starting points: "empty"
 * has no picks to show yet, "inline" sits above picks that could be sharper. Neither repeats the
 * completeness figure; the Profile Strength meter on the card below already carries it.
 */
const UPSELL_BODY = {
	empty:
		"We don't know enough about you to match you to jobs yet. Tell us your skills and the kind of role you're after, and you'll get personalized job recommendations that match your skills and preferences.",
	inline:
		"You've given us the basics. Add your skills, where you want to work and the kind of role you're after, and the recommended job picks below will be more personalized.",
};

/** Decorative only — the heading beside it already carries the message, so alt is empty. */
function UpsellIllustration({ className = "" }: { className?: string }) {
	return (
		<div className={`flex-1 ${className}`}>
			<div className="w-full aspect-2/1 relative">
				<img
					alt=""
					loading="lazy"
					decoding="async"
					className="object-contain absolute h-full w-full left-0 top-0 bottom-0 right-0"
					src="/images/solo-creation-transparent.png"
				/>
			</div>
		</div>
	);
}

interface PreferencesUpsellCardProps {
	/** "inline" is the banner at the top of the profile page; "empty" replaces the job list. */
	variant: "empty" | "inline";
	completenessPct: number;
	onOpenPreferences: () => void;
}

function PreferencesUpsellCard({ variant, completenessPct, onOpenPreferences }: PreferencesUpsellCardProps) {
	useEffect(() => {
		posthog.capture("preferences_upsell_shown", {
			variant,
			completeness_pct: completenessPct,
		});
	}, [variant, completenessPct]);

	function onClick() {
		posthog.capture("preferences_upsell_clicked", {
			variant,
			completeness_pct: completenessPct,
		});
		onOpenPreferences();
	}

	if (variant === "inline") {
		return (
			<div className="bg-blue-50/50 rounded-lg p-6 border border-blue-100 mb-4">
				<div className="flex flex-col sm:flex-row sm:items-center gap-6">
					<div className="flex-2">
						<h3 className="text-xl font-semibold tracking-normal text-gray-900 mb-2">Get better job matches</h3>
						{/* No progress bar — the profile card right below already shows Profile Strength. */}
						<p className="text-gray-800 text-sm mb-4">{UPSELL_BODY.inline}</p>
						<button
							type="button"
							onClick={onClick}
							className="inline-flex items-center justify-center shrink-0 bg-primary-600 text-white px-4 h-9 rounded hover:bg-primary-700 transition-colors text-sm font-medium"
						>
							Complete Profile
						</button>
					</div>
					<UpsellIllustration className="hidden sm:block" />
				</div>
			</div>
		);
	}

	return (
		<div className="bg-blue-50/50 rounded-lg p-6 border border-blue-100 h-full flex flex-col justify-between">
			<div className="flex justify-between items-center gap-8">
				<div className="flex-2">
					<span className="text-sm font-medium text-primary-600">Profile Completion</span>
					<h2 className="text-xl font-semibold tracking-normal text-gray-900 mt-1 mb-2">Get better job matches</h2>
					<p className="text-gray-800 text-sm mb-8">{UPSELL_BODY.empty}</p>
				</div>
				<UpsellIllustration />
			</div>
			<button
				type="button"
				onClick={onClick}
				className="inline-flex items-center justify-center bg-white text-gray-700 px-4 h-9 rounded hover:bg-gray-50 transition-colors text-sm font-medium border border-gray-300 w-full mt-8"
			>
				Complete Profile
			</button>
		</div>
	);
}

export default PreferencesUpsellCard;
